import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import {
  ModalTemplateComponent,
  PencilModalSizeEnum,
} from 'src/app/standalones/components/modals/modal-template/modal-template.component';
import { PencilSpinnerComponent } from 'src/app/ui/pencil-spinner/pencil-spinner.component';
import { SlidesAccessApiService } from 'src/app/services/slides-import/slides-access.service';
import { SlidesAccess } from 'src/app/models/slides-import.models';
export enum SlidesImportDialogMode {
  Checking = 'checking',
  Loading = 'loading',
}

export interface SlidesImportCheckingAccessDialogData {
  title: string;
  message: string;
  mode: SlidesImportDialogMode;
  presentationId?: string;
  /** When mode is 'checking' and access is Public, dialog switches to this title/message before resolving. */
  loadingTitle?: string;
  loadingMessage?: string;
}

/**
 * Dialog for checking access to a Google Slides presentation.
 * When mode is SlidesImportDialogMode.Loading, only shows spinner (caller closes).
 * When mode is SlidesImportDialogMode.Checking, runs access check and closes with result; requires presentationId.
 * When access is Public, dialog switches to loadingTitle/loadingMessage before resolving.
 */
@Component({
  selector: 'app-slides-import-checking-access-dialog',
  templateUrl: './slides-import-checking-access-dialog.component.html',
  styleUrls: ['./slides-import-checking-access-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ModalTemplateComponent, PencilSpinnerComponent],
})
export class SlidesImportCheckingAccessDialogComponent implements OnInit {
  readonly PencilModalSizeEnum = PencilModalSizeEnum;

  private accessResolve!: (value: SlidesAccess | false) => void;
  readonly whenAccessChecked: Promise<SlidesAccess | false>;

  constructor(
    private readonly dialogRef: MatDialogRef<
      SlidesImportCheckingAccessDialogComponent,
      SlidesAccess | false
    >,
    private readonly slidesAccessService: SlidesAccessApiService,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) protected readonly data: SlidesImportCheckingAccessDialogData,
  ) {
    this.whenAccessChecked = new Promise<SlidesAccess | false>((resolve) => {
      this.accessResolve = resolve;
    });
  }

  private setContent(title: string, message: string): void {
    this.data.title = title;
    this.data.message = message;
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    if (this.data.mode === SlidesImportDialogMode.Loading) {
      return;
    }
    this.checkAccessAndComplete();
  }

  onCancel(): void {
    this.accessResolve(false);
    this.dialogRef.close(false);
  }

  private async checkAccessAndComplete(): Promise<void> {
    try {
      const access = await firstValueFrom(
        this.slidesAccessService.checkAccess(this.data.presentationId!),
      );
      if (
        access === SlidesAccess.Public &&
        this.data.loadingTitle != null &&
        this.data.loadingMessage != null
      ) {
        this.setContent(this.data.loadingTitle, this.data.loadingMessage);
      }
      this.accessResolve(access);
      if (access !== SlidesAccess.Public) {
        this.dialogRef.close(access);
      }
    } catch {
      this.accessResolve(SlidesAccess.Restricted);
      this.dialogRef.close(SlidesAccess.Restricted);
    }
  }
}
